import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../framework/BaseWebComponentController';
import { AlertsDetailsPanelComponent } from '../component/AlertsDetailsPanelComponent';
import { AlertsDetailsPanelView } from '../view/AlertsDetailsPanelView';
import { templateAlertsDetailsPanel } from '../view/template';
import { DrillDownToBlockList, NavigationHelper, TaskTarget } from '@nirvana/shared/navigation';
import {
  ActionPermissionState,
  ActionsPermissions,
  DetailsPanelEvents,
  ExclusionRecord,
  GetEventDetailsResponseFrontend,
} from '../types';
import {
  RawEventDeviceDetails,
  RawEventDeviceInfo,
  RawEventThreatInfo,
} from '@nirvana/shared/dtos/types/raw-events/gz-console';
import { Feature } from '@nirvana/shared/constants';
import { Category, ThreatType } from '@nirvana/shared/dtos/types/shared/threats';
import { Module } from '../../configurationProfilesApp/exclusionsPanel/types';
import {
  ManagementStatus,
  NetworkColumn,
  NetworkGridAppExternalParams,
} from '../../networkV2/networkAppContainer/helpers/types';

export class AlertsDetailsPanelController extends BaseWebComponentController {
  public component: AlertsDetailsPanelComponent;
  public view: AlertsDetailsPanelView;
  private user;

  constructor(component: AlertsDetailsPanelComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: AlertsDetailsPanelView,
      viewTemplate: templateAlertsDetailsPanel,
    });
  }

  injectDependencies(dependencies) {
    super.injectDependencies(dependencies);
    this.user = this.dependencies.getUser();
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$sectionsDiv,
        event: DetailsPanelEvents.NAVIGATE_TO_NETWORK,
        callback: this.navigateToNetwork,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: DetailsPanelEvents.NAVIGATE_TO_DETAILS_PANEL,
        callback: this.navigateToEndpointDetailsPanel,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: DetailsPanelEvents.SECURITY_RISKS_DRILL_DOWN,
        callback: this.drillDownToSecurityRisks,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: DetailsPanelEvents.CREATE_EXCLUSIONS,
        callback: this.createExclusionsAndNavigateToConfigurationProfiles,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: DetailsPanelEvents.POLICY_DRILL_DOWN,
        callback: this.drillDownToPolicy,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: DetailsPanelEvents.ADD_TO_BLOCK_LIST,
        callback: this.drillDownToBlockList,
      },
    ];
  }

  private navigateToEndpointDetailsPanel(event: WebComponentEvent): void {
    NavigationHelper.navigateToNetworkAndShowDetailsPanel(event.detail.protectedEntityId);
  }

  async createExclusionsAndNavigateToConfigurationProfiles(ev: WebComponentEvent) {
    const configProfilesModule = this.dependencies.Ext.getModuleByName('configurationProfiles');
    const exclusionRecords = this.extractExclusionRecordsFromEvent(ev);

    await configProfilesModule.statics().createExclusionItem({ records: exclusionRecords });
    configProfilesModule.statics().showPage(true);
  }

  extractExclusionRecordsFromEvent(ev: WebComponentEvent): Array<ExclusionRecord> {
    const exclusionRecords: Array<ExclusionRecord> = [];
    const companyId = this.user.company.id;

    if (ev.detail.threatType === ThreatType.LSASS_PROCESS_CREATION) {
      exclusionRecords.push({
        companyId,
        type: this.dependencies.Ext.Constants.Reports.BASE.BLOCKED_APPLICATIONS.MODULE_TYPE_ANTIMALWARE,
        specifics: {
          type: this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_PROFILE.PROCESS,
          path: ev.detail.threatDetails,
          modules: [Module.LSASS_PROTECTION],
        },
      });

      return exclusionRecords;
    }

    exclusionRecords.push({
      companyId,
      type: this.dependencies.Ext.Constants.Reports.BASE.BLOCKED_APPLICATIONS.MODULE_TYPE_ANTIMALWARE,
      specifics: {
        type: this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_PROFILE.FILE,
        path: ev.detail.threatDetails,
        modules: [
          this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.ON_DEMAND,
          this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.ON_ACCESS,
        ],
      },
    });

    if (ev.detail.threatCategory === Category.PROCESS) {
      exclusionRecords.push({
        companyId,
        type: this.dependencies.Ext.Constants.Reports.BASE.BLOCKED_APPLICATIONS.MODULE_TYPE_ANTIMALWARE,
        specifics: {
          type: this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_PROFILE.PROCESS,
          path: ev.detail.threatDetails,
          modules: [
            this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.ON_ACCESS,
            this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.AVC_IDS,
            this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.RANSOMWARE_REMEDIATION,
            this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.LSASS_PROTECTION,
            this.dependencies.Ext.Constants.Policies.Endpoints.ANTIMALWARE_EXCLUSIONS_MODULE.DPI_DETECTIONS,
          ],
        },
      });
    }

    return exclusionRecords;
  }

  navigateToNetwork(ev: WebComponentEvent) {
    const networkV2NavigationParams: NetworkGridAppExternalParams = {
      filters: [
        {
          type: {
            in: [
              Constants.Network.Filters.Types.COMPUTERS,
              Constants.Network.Filters.Types.VIRTUAL_MACHINES,
              Constants.Network.Filters.Types.CONTAINERS,
              Constants.Network.Filters.Types.GOLDEN_IMAGES,
            ],
          },
        },
        {
          managementStatus: {
            in: [ManagementStatus.MANAGED_NETWORK_V2],
          },
        },
        {
          company: {
            equals: ev.detail.company.id,
          },
        },
        {
          name: {
            equals: ev.detail.endpointName,
          },
        },
      ],
      treeDetails: {
        targetId: ev.detail.company.id,
        type: Constants.Companies.TYPE_COMPANY,
        path: ev.detail.company.path,
        parentId: '', // not required
      },
      displayedColumns: [
        NetworkColumn.NAME,
        NetworkColumn.COMPANY,
        NetworkColumn.IP,
        NetworkColumn.TYPE,
        NetworkColumn.MANAGEMENT_STATUS,
        NetworkColumn.SECURITY_ISSUES,
        NetworkColumn.LAST_SEEN,
      ],
      recursive: true,
    };

    NavigationHelper.navigateToNetworkV2(networkV2NavigationParams);
  }

  async getDataForView(): Promise<GetEventDetailsResponseFrontend> {
    this.view.setLoadingState();

    const response = await this.dependencies.api.clients.RawEvents.getEventDetails({
      eventId: this.component.eventId,
    }).toPromise();

    this.view.removeLoadingState();
    const permissions = this.getActionsPermissions(response.affectedDevice, response.detectionDetails);

    return {
      ...response,
      allColumnsDetails: this.component.allColumnDetails,
      isPartner: this.component.isPartner,
      taskTarget: this.getTaskTargetData(response.affectedDevice.endpoint, permissions),
      permissions,
    };
  }

  private getActionsPermissions(affectedDevice: RawEventDeviceInfo, data: RawEventThreatInfo): ActionsPermissions {
    return {
      scan: this.getEndpointPermissions(affectedDevice.endpoint),
      isolate: this.getIsolatePermissions(affectedDevice.endpoint),
      endpointDetails: this.getEndpointPermissions(affectedDevice.endpoint),
      alerts: ActionPermissionState.ACTIVE,
      blocklist: this.getBlocklistPermissions(data),
      exclusion: this.getPolicyWritePermissions(),
      network: this.getNetworkPermissions(),
      policy: this.getPolicyPermissions(affectedDevice),
      riskScore: this.getRiskScorePermissions(),
    };
  }

  private getTaskTargetData(endpoint: RawEventDeviceDetails, permissions: ActionsPermissions): TaskTarget | undefined {
    if (![permissions.scan, permissions.isolate].includes(ActionPermissionState.ACTIVE)) {
      return undefined;
    }

    return {
      protectedEntityId: endpoint.protectedEntityId as string,
      type: endpoint.protectedEntityType as number,
      specifics: {
        appId: endpoint.appId as number,
        endpointId: endpoint.id,
      },
    };
  }

  private endpointHasRequiredData(endpoint: RawEventDeviceDetails) {
    return endpoint.protectedEntityId && endpoint.protectedEntityType && endpoint.appId;
  }

  private drillDownToPolicy(event: WebComponentEvent): void {
    const policyId = event.detail.policyId;
    NavigationHelper.viewPolicyDetails(policyId);
  }

  private drillDownToSecurityRisks(event: WebComponentEvent): void {
    NavigationHelper.navigateToRiskManagement(event.detail);
  }

  private getEndpointPermissions(endpoint: RawEventDeviceDetails): ActionPermissionState {
    if (endpoint.lostAccess) {
      return ActionPermissionState.LOST_ACCESS;
    }

    if (endpoint.isRemoved) {
      return ActionPermissionState.REMOVED;
    }

    if (!this.endpointHasRequiredData(endpoint)) {
      return ActionPermissionState.OTHER;
    }

    return this.getNetworkPermissions();
  }

  private getBlocklistPermissions(data: RawEventThreatInfo): ActionPermissionState {
    const userHasAccessToBlockList = this.dependencies.Ext.hasFeatures([Feature.PRODUCT_FEATURE_EDR_BLOCKLIST]);

    if (!userHasAccessToBlockList) {
      return ActionPermissionState.MISSING_BLOCKLIST;
    }

    if (!data.sha256) {
      return ActionPermissionState.NO_HASH;
    }

    const networkPermissions: ActionPermissionState = this.getNetworkPermissions();
    return networkPermissions === ActionPermissionState.ACTIVE ? this.getPolicyWritePermissions() : networkPermissions;
  }

  private getPolicyPermissions(affectedDevice: RawEventDeviceInfo) {
    if (!affectedDevice.policy || affectedDevice.policy.isRemoved) {
      return ActionPermissionState.NO_POLICY;
    }

    return this.getPolicyReadonlyPermissions();
  }

  private getRiskScorePermissions(): ActionPermissionState {
    const manageInventory = this.user.role.rights.manageInventory;

    if (!manageInventory) {
      return ActionPermissionState.MISSING_NETWORK;
    }

    const hasEraFeature = this.dependencies.Ext.hasFeatures([Feature.PRODUCT_FEATURE_IOR_DATA]);
    if (!hasEraFeature) {
      return ActionPermissionState.MISSING_FEATURE;
    }

    return ActionPermissionState.ACTIVE;
  }

  private getNetworkPermissions(): ActionPermissionState {
    const manageInventory = this.user.role.rights.manageInventory;

    if (!manageInventory) {
      return ActionPermissionState.MISSING_NETWORK;
    }

    return ActionPermissionState.ACTIVE;
  }

  private getPolicyReadonlyPermissions(): ActionPermissionState {
    const managePoliciesRead = this.user.role.rights.managePoliciesRead;

    if (!managePoliciesRead) {
      return ActionPermissionState.MISSING_POLICIES_READ;
    }

    return ActionPermissionState.ACTIVE;
  }

  private getPolicyWritePermissions(): ActionPermissionState {
    const managePoliciesWrite = this.user.role.rights.managePoliciesWrite;

    if (!managePoliciesWrite) {
      return ActionPermissionState.MISSING_POLICIES_WRITE;
    }

    return ActionPermissionState.ACTIVE;
  }

  private getIsolatePermissions(endpoint: RawEventDeviceDetails): ActionPermissionState {
    const isPartner = this.component.isPartner;

    const hasIsolationVisible = this.dependencies.Ext.hasFeatures([Feature.PRODUCT_FEATURE_ISOLATION_VISIBLE]);
    const hasIsolationUsable = this.dependencies.Ext.hasFeatures([Feature.PRODUCT_FEATURE_ISOLATION_USABLE]);

    // For partners with isolationVisible, show the button
    // For customers with isolationUsable, show the button
    // If neither of the above cases, the button is missing
    if (!((isPartner && hasIsolationVisible) || (!isPartner && hasIsolationUsable))) {
      return ActionPermissionState.MISSING_ISOLATE;
    }

    // Only these types of endpoints can have an isolate task
    if (
      !endpoint.isRemoved &&
      endpoint.appId !== Constants.Endpoints.APP_IDS.APPID_UNIFIED_CLIENT &&
      !endpoint.isContainerHost
    ) {
      return ActionPermissionState.WRONG_ENDPOINT_TYPE;
    }

    if (endpoint.isIsolated) {
      return ActionPermissionState.ISOLATED;
    }

    return this.getEndpointPermissions(endpoint);
  }

  private drillDownToBlockList(event: WebComponentEvent) {
    const data = event.detail as DrillDownToBlockList;
    NavigationHelper.showAndFillBlockListForm(this.dependencies.Ext, data);
  }
}
